import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Clock, Sparkles, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogListPage() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const res = await axios.get(`${API}/public/blogs`);
      setBlogs(res.data);
    } catch (err) {
      console.error('Error fetching blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="blog-list-page">
      {/* Navbar */}
      <nav className="bg-black/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-violet-700 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">Law Agency 360</span>
            </button>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="text-zinc-400 hover:text-white transition-colors text-sm font-medium"
              >
                Home
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-violet-500/25"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative py-20 px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_#111111_0%,_#000000_50%)]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-violet-600/10 rounded-full blur-[120px]"></div>
        <div className="relative max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-500">Blog</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Insights and strategies on digital marketing, SEO, AI visibility, and growing your firm's online presence.
          </p>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        {loading ? (
          <div className="text-center py-20 text-zinc-500">Loading articles...</div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-500 text-lg">No articles published yet. Check back soon.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.map((blog) => (
              <article
                key={blog.id}
                onClick={() => navigate(`/blog/${blog.slug}`)}
                className="group cursor-pointer bg-white/[0.02] hover:bg-white/[0.05] backdrop-blur-sm border border-white/[0.05] hover:border-violet-500/30 rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
                data-testid={`blog-card-${blog.slug}`}
              >
                {blog.featured_image ? (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={blog.featured_image}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-violet-600/20 to-purple-600/20 flex items-center justify-center">
                    <Sparkles className="w-10 h-10 text-violet-400/50" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-zinc-500 text-xs mb-3">
                    <Clock className="w-3.5 h-3.5" />
                    {blog.published_at ? new Date(blog.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                  </div>
                  <h2 className="text-xl font-semibold text-white mb-2 group-hover:text-violet-400 transition-colors line-clamp-2">
                    {blog.title}
                  </h2>
                  {blog.excerpt && (
                    <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3 mb-4">{blog.excerpt}</p>
                  )}
                  <span className="inline-flex items-center gap-1.5 text-violet-400 text-sm font-medium group-hover:gap-2.5 transition-all">
                    Read More <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="relative py-12 px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center text-zinc-500 text-sm">
          &copy; 2026 Law Agency 360. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
